import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import ImprovedFlow from '@/components/ImprovedFlow';
import ROICalculator from '@/components/ROICalculator';
import LiveSocialProof from '@/components/LiveSocialProof';
import Footer from '@/components/Footer';
import { fadeIn } from '@/utils/animations';
import { Button } from '@/components/ui/button';
import { ArrowRight, Zap, Clock, Target, TrendingUp, Users, Mail, Award } from 'lucide-react';

const LaunchPage = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    console.log('🚀 Launch Page - Interactive Flow Loaded!');
  }, []);

  // Static background effects for better performance
  const StaticBackgroundEffects = () => (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-r from-purple-300/30 via-blue-300/20 to-pink-300/30 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-[400px] h-[400px] bg-gradient-to-l from-blue-300/25 via-cyan-300/20 to-purple-300/25 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-indigo-300/20 via-purple-300/15 to-pink-300/20 rounded-full blur-3xl" />
    </div>
  );

  return (
    <motion.div 
      className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-slate-50 relative overflow-hidden"
      initial="initial"
      animate="animate"
      variants={fadeIn}
    >
      {/* Simplified background layers */}
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-purple-100/40 via-transparent to-transparent" />
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-blue-100/40 via-transparent to-transparent" />
      
      {/* Static background effects for better performance */}
      <StaticBackgroundEffects />
      
      {/* Static grid pattern (no animation) */}
      <div 
        className="fixed inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,0,0,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }}
      />
      
      {/* Main content */}
      <div className="relative z-10">
        {/* Improved Flow Section */}
        <ImprovedFlow />
        
        {/* Live Social Proof */}
        <LiveSocialProof />
        
        {/* 3-Step Visual Workflow */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50/50 via-purple-50/30 to-green-50/50" />
          
          <div className="max-w-6xl mx-auto px-6 relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 border border-blue-200 mb-6">
                <Zap className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-semibold text-blue-700">Autonomous AI Process</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  How Your AI Marketer Works
                </span>
              </h2>
              
              <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                Create a "Product" in 2 minutes → AI handles everything else automatically
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: <Users className="w-12 h-12" />,
                  title: "You Create a \"Product\"",
                  description: "Tell AI about your business in 2 minutes - your offering, target market, and goals",
                  color: "from-blue-500 to-cyan-500",
                  bgColor: "from-blue-50 to-cyan-50"
                },
                {
                  icon: <Mail className="w-12 h-12" />,
                  title: "AI Sets Up Everything",
                  description: "AI automatically configures servers, finds prospects, writes emails, and launches campaigns",
                  color: "from-purple-500 to-pink-500",
                  bgColor: "from-purple-50 to-pink-50"
                },
                {
                  icon: <Target className="w-12 h-12" />,
                  title: "AI Runs on Autopilot",
                  description: "AI sends emails, responds to replies, books meetings, and self-optimizes 24/7",
                  color: "from-green-500 to-emerald-500",
                  bgColor: "from-green-50 to-emerald-50"
                }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="text-center group"
                >
                  <div className={`inline-flex p-6 rounded-2xl bg-gradient-to-r ${step.bgColor} border border-current/20 mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <div className={`text-transparent bg-gradient-to-r ${step.color} bg-clip-text`}>
                      {step.icon}
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                  
                  {index < 2 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                      <ArrowRight className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        
        {/* ROI Calculator */}
        <ROICalculator />
        
        <Footer />
      </div>
    </motion.div>
  );
};

export default LaunchPage; 